import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Injector, Input, OnChanges, OnInit, Output, ViewChild } from "@angular/core";
import { TeamType } from "@common/ADAPT.Common.Model/organisation/team-type";
import { Vision } from "@common/ADAPT.Common.Model/organisation/vision";
import { VisionCategory } from "@common/ADAPT.Common.Model/organisation/vision-category";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { ElementUtilities } from "@common/lib/utilities/element-utilities";
import { RouteService } from "@common/route/route.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { IConfirmationDialogData } from "@common/ux/adapt-common-dialog/confirmation-dialog.component/confirmation-dialog.component";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { IAdaptMenuItem, MenuComponent } from "@common/ux/menu/menu.component";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { guidingPhilosophyRoute } from "@org-common/lib/guiding-philosophy/guiding-philosophy-page/guiding-philosophy-page.route";
import { CommonPurposeVisionService } from "@org-common/lib/purpose-vision/common-purpose-vision.service";
import { CommonPurposeVisionAuthService } from "@org-common/lib/purpose-vision/common-purpose-vision-auth.service";
import dxAccordion, { ContentReadyEvent } from "devextreme/ui/accordion";
import { DxAccordionComponent } from "devextreme-angular";
import { BehaviorSubject, debounceTime, filter, first, forkJoin, switchMap } from "rxjs";
import { VisionService } from "../vision.service";
import { VisionAuthService } from "../vision-auth.service";

export const ReturnCategoryKey = "returnCategory";

// Allow category to be hidden:
// https://js.devexpress.com/Angular/Documentation/24_2/ApiReference/UI_Components/dxAccordion/Configuration/items/#visible
interface IVisibleItem {
    visible?: boolean;
}

@Component({
    selector: "adapt-category-vision-accordion",
    standalone: false,
    templateUrl: "./category-vision-accordion.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./category-vision-accordion.component.scss",
})
export class CategoryVisionAccordionComponent extends BaseComponent implements OnChanges, OnInit {
    public readonly SummaryCategory = {
        visionCategoryId: 0,
        name: "Summary",
        description: "Visions without a category",
        visible: true,
    } as VisionCategory & IVisibleItem;

    @Input() public personId?: number;
    @Input() public teamId?: number;

    @Input() public integratedOnly = false;
    @Input() public expandAll = false;
    @Input() public indicateIncompleteCategories = false;
    @Input() public showExpandCollapseMenu = false;
    @Input() public personColorIndex?: number;

    @Output() public initialised = new EventEmitter<CategoryVisionAccordionComponent>();

    public categories?: VisionCategory[];
    public visions: Vision[] = [];
    public publishVisionMenuItem: IAdaptMenuItem = {
        text: "Publish completed vision",
        icon: "fal fa-fw fa-share-all",
        visible: false,
        separatorBottom: true,
        onClick: () => this.publishIntegratedVision(),
    };
    public editIntegratedVisionMenuItem: IAdaptMenuItem = {
        text: "Edit integrated vision",
        icon: "fal fa-fw fa-edit",
        separatorTop: true,
        onClick: () => this.editIntegratedVision(),
    };
    public assignIntegratedVisionMenuItem: IAdaptMenuItem = {
        text: "Assign integrated vision",
        icon: "fal fa-fw fa-layer-plus",
        onClick: () => this.assignIntegratedVision(),
    };
    public recaptureVisionMenuItem: IAdaptMenuItem = {
        text: "Recapture vision",
        icon: "fal fa-fw fa-arrows-retweet",
        separatorTop: true,
        onClick: () => this.visionService.openCaptureVisionDialog(this.teamId!).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(),
    };
    public menuItems = [{
        text: "",
        icon: MenuComponent.SmallRootMenu.icon,
        items: [
            this.publishVisionMenuItem,
            {
                text: "Expand all",
                icon: "fal fa-fw fa-chevrons-down",
                onClick: () => this.expandCategories(),
            },
            {
                text: "Collapse all",
                icon: "fal fa-fw fa-chevrons-up",
                onClick: () => this.collapseCategories(),
            },
            this.editIntegratedVisionMenuItem,
            this.assignIntegratedVisionMenuItem,
            this.recaptureVisionMenuItem,
        ],
    }];
    public lastPublished?: Date;
    public integratedVisionPersonId?: number;
    public canEditTeamVision = false;

    @ViewChild(DxAccordionComponent) public accordion?: DxAccordionComponent;

    private triggerUpdate$ = new BehaviorSubject<void>(undefined);

    public constructor(
        private visionService: VisionService,
        private visionAuthService: VisionAuthService,
        private injector: Injector,
        private routeService: RouteService,
        private authService: AuthorisationService,
        rxjsBreezeService: RxjsBreezeService,
        elementRef: ElementRef,
    ) {
        super(elementRef);

        rxjsBreezeService.entityTypeChanged(Vision).pipe(
            filter((vision) => !!this.teamId && vision.teamId === this.teamId),
            debounceTime(300), // debounce to allow multiple changes to be processed together
            this.takeUntilDestroyed(),
        ).subscribe(() => this.triggerUpdate$.next());
    }

    public ngOnChanges() {
        if (this.teamId) {
            const getVisions$ = this.integratedOnly
                ? this.visionService.getIntegratedVisionsByTeam(this.teamId)
                : this.visionService.getVisionsByPersonTeam(this.personId, this.teamId);
            this.triggerUpdate$.pipe(
                switchMap(() => forkJoin([
                    this.visionService.getVisionCategoriesForTeam(this.teamId!),
                    this.visionAuthService.canEditVisionForTeamId(this.teamId!),
                    getVisions$,
                    this.authService.getHasAccess(CommonPurposeVisionAuthService.EditPurposeVision).pipe(first()),
                ])),
                this.takeUntilDestroyed(),
            ).subscribe(([categories, canEditTeamVision, visions, canEditOrgVision]) => {
                this.categories = categories;
                this.canEditTeamVision = canEditTeamVision;
                this.visions = visions.sort(this.visionService.defaultVisionSortFunction);
                const integratedVisions = this.visions.filter((v) => v.extensions.isIntegrated);
                const integratedSummary = visions.find((v) => v.extensions.isSummary);
                if (integratedSummary) {
                    // summary without category
                    if (this.categories && !this.categories.includes(this.SummaryCategory)) {
                        this.categories.unshift(this.SummaryCategory);
                    }

                    this.SummaryCategory.visible = !!integratedSummary.statement || // if there is summary - show it
                        (integratedVisions.length > 0 && integratedVisions.some((v) => v.extensions.isCompleted));
                }

                if (this.visions[0]?.team.teamType === TeamType.Owner) {
                    // only offer publishing to vision from owner team
                    this.publishVisionMenuItem.visible = canEditOrgVision && (this.visionService.areAllVisionsCompleted(integratedVisions) && integratedVisions.length > 0);
                }

                const summaryIntegratedVision = this.visions.find((v) => v.extensions.isSummary && v.extensions.isIntegrated);
                if (summaryIntegratedVision) {
                    this.integratedVisionPersonId = summaryIntegratedVision.personId;
                    if (this.visionService.areAllVisionsCompleted(this.visions)) {
                        this.lastPublished = summaryIntegratedVision.lastPublished;
                    }
                }

                this.editIntegratedVisionMenuItem.visible = this.canEditTeamVision;
                this.assignIntegratedVisionMenuItem.visible = this.canEditTeamVision;
                this.recaptureVisionMenuItem.visible = this.canEditTeamVision;
            });
        }
    }

    public ngOnInit() {
        this.initialised.emit(this);

        const self = this;
        const returnCategory = this.routeService.getSearchParameterIntValue(ReturnCategoryKey);
        if (returnCategory !== undefined && !isNaN(returnCategory)) {
            scrollToCategory(returnCategory);
        }

        function scrollToCategory(id: number) {
            const element = document.getElementById(`vision-category-${id}`);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
                self.routeService.deleteSearchParameter(ReturnCategoryKey, true, true);
            } else {
                setTimeout(() => scrollToCategory(id), 500);
            }
        }
    }

    public getVisionsForCategory(categoryId: number) {
        return this.visions.filter((vision) => (vision.visionCategoryId === categoryId) || (categoryId === 0 && !vision.visionCategoryId));
    }

    public onContentReady(e: ContentReadyEvent) {
        if (this.expandAll) {
            this.expandCategories(e.component);
        }

        // need this to get rid of an occasional accordion not refreshed property issue
        if (this.elementRef?.nativeElement) {
            ElementUtilities.waitUntilVisible(this.elementRef.nativeElement).pipe(
                this.takeUntilDestroyed(),
            ).subscribe(() => this.accordion?.instance.updateDimensions());
        }
    }

    public expandCategories(accordion?: dxAccordion) {
        accordion = accordion ?? this.accordion?.instance;
        if (accordion && this.categories?.length) {
            this.categories.forEach((_, index) => accordion.expandItem(index));
        }
    }

    public collapseCategories() {
        if (this.accordion?.instance && this.categories?.length) {
            this.categories.forEach((_, index) => this.accordion?.instance.collapseItem(index));
        }
    }

    public editVision(vision: Vision) {
        if (vision.extensions.isPersonal) {
            this.editVisionForPerson(vision.personId!, vision.visionCategoryId!);
        } else {
            this.editIntegratedVision(vision.visionCategoryId ?? 0);
        }
    }

    public editVisionForPerson(personId: number, categoryId: number) {
        if (this.teamId && personId) {
            return this.visionService.editVisionForPerson(personId, this.teamId, categoryId, "Return to team vision page", true).subscribe();
        }
    }

    public editIntegratedVision(categoryId?: number) {
        if (this.teamId) {
            return this.visionService.editIntegratedVision(this.teamId, categoryId, "Return to team vision page", true).subscribe();
        }
    }

    public hasCompletedPersonalVision(vision?: Vision) {
        return !!vision && this.visions.some((v) => v.extensions.isPersonal && v.visionCategoryId === vision.visionCategoryId && v.extensions.isCompleted);
    }

    public getIntegratedVision(visions: Vision[]) {
        return visions.find((v) => v.extensions.isIntegrated);
    }

    private assignIntegratedVision() {
        this.visionService.assignIntegratedVision(this.teamId!).pipe(
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    private publishIntegratedVision() {
        // only inject this when publishing
        const commonPurposeVisionService = this.injector.get(CommonPurposeVisionService);
        const dialogService = this.injector.get(AdaptCommonDialogService);
        dialogService.openConfirmationDialog({
            title: "Publish integrated vision",
            message: `<p>Are you sure you want to publish the integrated vision to the organisation?</p>
                  <p>This will overwrite the existing published vision. Ensure you have a backup if you need to retain the current version.</p>
                  <p>Do you want to continue?</p>`,
            confirmButtonText: "Publish",
        } as IConfirmationDialogData).pipe(
            switchMap(() => commonPurposeVisionService.getOrCreateVision()),
            switchMap((visionEntity) => {
                const changedEntities: IBreezeEntity[] = [visionEntity];
                visionEntity.vision = this.visionService.extractIntegratedVisionText(this.visions);
                // update the timestamp summary integrated vision
                const summaryIntegratedVision = this.visions.find((v) => v.extensions.isSummary && v.extensions.isIntegrated);
                if (summaryIntegratedVision) {
                    summaryIntegratedVision.lastPublished = new Date();
                    changedEntities.push(summaryIntegratedVision);
                    this.lastPublished = summaryIntegratedVision.lastPublished;
                }

                return commonPurposeVisionService.saveEntities(changedEntities);
            }),
            this.takeUntilDestroyed(),
        ).subscribe(() => setTimeout(() => guidingPhilosophyRoute.gotoRoute().subscribe()));
    }
}
